import * as React from 'react';
import {
  Box,
  Button,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useNavigate } from 'react-router';
import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import ImagePicker from '../components/ImagePicker';
import { RichTextEditor } from '../components/RichTextEditor';

const API_URL = import.meta.env.VITE_API_URL;

function useDebounce(value: string, delay = 400) {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

const checkboxStyle = `
  .purple-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 6px 10px;
    border-radius: 8px;
    border: 1.5px solid #e0d7f5;
    background: #faf8ff;
    transition: border-color 0.15s, background 0.15s;
    font-size: 14px;
    color: #3d2a6e;
    user-select: none;
  }
  .purple-checkbox:hover {
    border-color: #7c3aed;
    background: #f3eeff;
  }
  .purple-checkbox input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 17px;
    height: 17px;
    border: 2px solid #a78bfa;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    position: relative;
    flex-shrink: 0;
    transition: background 0.15s, border-color 0.15s;
  }
  .purple-checkbox input[type="checkbox"]:checked {
    background: #7c3aed;
    border-color: #7c3aed;
  }
  .purple-checkbox input[type="checkbox"]:checked::after {
    content: '';
    position: absolute;
    left: 3px;
    top: 0px;
    width: 5px;
    height: 9px;
    border: 2px solid #fff;
    border-top: none;
    border-left: none;
    transform: rotate(45deg);
  }
  .purple-checkbox.checked {
    border-color: #7c3aed;
    background: #f3eeff;
  }
`;

export default function BroadcastAdd() {
  const navigate = useNavigate();

  const [message, setMessage] = React.useState('');
  const [channelType, setChannelType] = React.useState('all');
  const [auditory, setAuditory] = React.useState('all');
  const [errorMessage, setErrorMessage] = React.useState('');

  const [regions, setRegions] = React.useState<any[]>([]);
  const [cities, setCities] = React.useState<any[]>([]);

  const [selectedRegions, setSelectedRegions] = React.useState<number[]>([]);
  const [selectedCities, setSelectedCities] = React.useState<number[]>([]);

  const [allRegions, setAllRegions] = React.useState(false);
  const [allCities, setAllCities] = React.useState(false);
  const [geoAll, setGeoAll] = React.useState(false);

  const [regionSearch, setRegionSearch] = React.useState('');
  const [citySearch, setCitySearch] = React.useState('');

  const debouncedRegionSearch = useDebounce(regionSearch);
  const debouncedCitySearch = useDebounce(citySearch);

  const [image, setImage] = React.useState<File | null>(null);
  const [mediaId, setMediaId] = React.useState<number | null>(null);
  const [imageUrl, setImageUrl] = React.useState<string | null>(null);

  const [scheduledDate, setScheduledDate] = React.useState<Dayjs | null>(dayjs());
  const [scheduledTime, setScheduledTime] = React.useState<Dayjs | null>(dayjs());

  React.useEffect(() => {
    if (allRegions || geoAll) return;

    fetch(`${API_URL}/region?search=${debouncedRegionSearch}`)
      .then((r) => r.json())
      .then((r) => setRegions(r.data));
  }, [debouncedRegionSearch, allRegions, geoAll]);

  React.useEffect(() => {
    if (geoAll) {
      setCities([]);
      return;
    }

    if (!selectedRegions.length && !allRegions) {
      setCities([]);
      return;
    }

    if (allCities && !allRegions) {
      fetch(
        `${API_URL}/region-cities?search=&regionIds=${selectedRegions.join(',')}&limit=30`,
      )
        .then((r) => r.json())
        .then((r) => setCities(r.data));
      return;
    }

    if (allRegions || allCities) {
      setCities([]);
      return;
    }

    fetch(
      `${API_URL}/region-cities?search=${debouncedCitySearch}&regionIds=${selectedRegions.join(',')}&limit=30`,
    )
      .then((r) => r.json())
      .then((r) => setCities(r.data));
  }, [selectedRegions, debouncedCitySearch, allRegions, allCities, geoAll]);

  const save = () => {
    if (!message || message.trim() === '') {
      setErrorMessage('Повідомлення не може бути пустим');
      return;
    }

    setErrorMessage('');

    const formData = new FormData();

    formData.append('message', message);
    formData.append('geoAll', String(geoAll));
    formData.append(
      'regionIds',
      geoAll ? '' : allRegions ? 'all' : selectedRegions.join(','),
    );
    formData.append(
      'cityIds',
      geoAll ? '' : allCities ? 'all' : selectedCities.join(','),
    );
    formData.append(
      'scheduledDateAt',
      scheduledDate ? scheduledDate.format('DD.MM.YYYY') : dayjs().format('DD.MM.YYYY'),
    );
    formData.append(
      'scheduledTimeAt',
      scheduledTime ? scheduledTime.format('HH:mm') : dayjs().format('HH:mm'),
    );
    formData.append('channelType', channelType);
    formData.append('auditory', auditory);

    if (image) {
      formData.append('image', image);
    } else if (mediaId !== null) {
      formData.append('mediaId', String(mediaId));
    }

    fetch(`${API_URL}/broadcast`, {
      method: 'POST',
      body: formData,
    }).then(() => navigate('/broadcasts'));
  };

  const showGeoSelector = !geoAll;
  const showCities =
    showGeoSelector && (allRegions || selectedRegions.length > 0 || allCities);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <style>{checkboxStyle}</style>

      <Box sx={{ maxWidth: 900 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Додати нову розсилку
        </Typography>

        <RichTextEditor
          value={message}
          onChange={(html) => setMessage(html)}
          label="Повідомлення"
          minRows={4}
        />

        {errorMessage && (
          <Typography color="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
        )}

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Канал</InputLabel>
          <Select
            value={channelType}
            onChange={(e) => setChannelType(e.target.value)}
          >
            <MenuItem value="viber">Viber</MenuItem>
            <MenuItem value="telegram">Telegram</MenuItem>
            <MenuItem value="all">Всі</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Аудиторія</InputLabel>
          <Select
            value={auditory}
            onChange={(e) => setAuditory(e.target.value)}
          >
            <MenuItem value="new">Нова</MenuItem>
            <MenuItem value="bot">Аудиторія бота</MenuItem>
            <MenuItem value="all">Всі</MenuItem>
          </Select>
        </FormControl>

        {/* geoAll toggle */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            border: '1.5px solid',
            borderColor: geoAll ? '#7c3aed' : '#e0d7f5',
            background: geoAll ? '#f3eeff' : '#faf8ff',
            transition: 'border-color 0.15s, background 0.15s',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={geoAll}
                onChange={(e) => setGeoAll(e.target.checked)}
                sx={{
                  color: '#a78bfa',
                  '&.Mui-checked': { color: '#7c3aed' },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: 14,
                  color: geoAll ? '#5b21b6' : '#3d2a6e',
                  fontWeight: geoAll ? 600 : 400,
                }}
              >
                Розсилати повідомлення всім користувачам незалежно від області або міста
              </Typography>
            }
          />
        </Box>

        {/* Regions */}
        {showGeoSelector && (
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ mb: 1 }}>Області</Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {allRegions ? (
                <Box
                  sx={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1.5px solid #7c3aed',
                    background: '#f3eeff',
                    color: '#7c3aed',
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                >
                  Обрані всі області
                </Box>
              ) : (
                <input
                  placeholder="Пошук області..."
                  value={regionSearch}
                  onChange={(e) => setRegionSearch(e.target.value)}
                  style={{
                    flex: 1,
                    padding: 8,
                    borderRadius: 6,
                    border: '1.5px solid #d1c4e9',
                    outline: 'none',
                  }}
                />
              )}

              <Button
                variant="outlined"
                sx={{
                  borderColor: '#7c3aed',
                  color: '#7c3aed',
                  '&:hover': { borderColor: '#5b21b6', background: '#f3eeff' },
                }}
                onClick={() => {
                  setAllRegions(true);
                  setSelectedRegions([]);
                  setSelectedCities([]);
                  setAllCities(false);
                }}
              >
                Обрати всі
              </Button>

              <Button
                variant="outlined"
                sx={{ borderColor: '#9ca3af', color: '#6b7280' }}
                onClick={() => {
                  setAllRegions(false);
                  setSelectedRegions([]);
                  setSelectedCities([]);
                  setAllCities(false);
                  setRegionSearch('');
                }}
              >
                Скасувати
              </Button>
            </Box>

            {!allRegions && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1,
                }}
              >
                {regions.map((r) => {
                  const checked = selectedRegions.includes(r.regionId);
                  return (
                    <label
                      key={r.regionId}
                      className={`purple-checkbox${checked ? ' checked' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          if (checked) {
                            setSelectedRegions(
                              selectedRegions.filter((rid) => rid !== r.regionId),
                            );
                          } else {
                            setSelectedRegions([...selectedRegions, r.regionId]);
                          }
                          setAllCities(false);
                        }}
                      />
                      {r.regionName}
                    </label>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {showCities && <Box sx={{ my: 2, fontSize: 14, color: 'text.secondary' }}>
          Якщо для обраної області не вказано місто, повідомлення буде надіслано до всіх міст цієї області.
        </Box> }

        {/* Cities */}
        {showCities && (
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ mb: 1 }}>Міста</Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {allCities ? (
                <Box
                  sx={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1.5px solid #7c3aed',
                    background: '#f3eeff',
                    color: '#7c3aed',
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                >
                  Обрані всі міста в областях
                </Box>
              ) : (
                <input
                  placeholder="Пошук міста..."
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                  style={{
                    flex: 1,
                    padding: 8,
                    borderRadius: 6,
                    border: '1.5px solid #d1c4e9',
                    outline: 'none',
                  }}
                />
              )}

              <Button
                variant="outlined"
                sx={{
                  borderColor: '#7c3aed',
                  color: '#7c3aed',
                  '&:hover': { borderColor: '#5b21b6', background: '#f3eeff' },
                }}
                onClick={() => {
                  setAllCities(true);
                  setSelectedCities([]);
                }}
              >
                Обрати всі
              </Button>

              <Button
                variant="outlined"
                sx={{ borderColor: '#9ca3af', color: '#6b7280' }}
                onClick={() => {
                  setAllCities(false);
                  setSelectedCities([]);
                  setCitySearch('');
                }}
              >
                Скасувати
              </Button>
            </Box>

            {!allCities && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1,
                }}
              >
                {cities.map((c) => {
                  const checked = selectedCities.includes(c.citiesId);
                  return (
                    <label
                      key={c.citiesId}
                      className={`purple-checkbox${checked ? ' checked' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          if (checked) {
                            setSelectedCities(
                              selectedCities.filter((cid) => cid !== c.citiesId),
                            );
                          } else {
                            setSelectedCities([...selectedCities, c.citiesId]);
                          }
                        }}
                      />
                      {c.name}
                    </label>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* Date & Time */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <DatePicker
            label="Дата відправки"
            value={scheduledDate}
            format="DD.MM.YYYY"
            onChange={(v) => setScheduledDate(v)}
          />
          <TimePicker
            label="Час відправки"
            value={scheduledTime}
            onChange={(v) => setScheduledTime(v)}
            ampm={false}
          />
        </Box>

        <ImagePicker
          title="Зображення"
          value={imageUrl || ''}
          onChange={(value) => {
            if (value.type === 'file') {
              setImage(value.file);
              setMediaId(null);
              setImageUrl(URL.createObjectURL(value.file));
            }

            if (value.type === 'media') {
              setImage(null);
              setMediaId(value.mediaId);
              setImageUrl(value.url);
            }
          }}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={save}>
            Створити
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Скасувати
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}